import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@ais-ui/ui-core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { StepperFooter } from './StepperFooter';
import { StepperHeadline } from './StepperHeadline';
import React, { useEffect, useMemo, useRef } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../../../conversions/useDateFormat';
import { useCreateReportContext } from './CreateReportContext';
import {
  createReportTemplatePeriodFormSchema,
  reportTemplatePeriodValues,
} from '../utils/createPeriodFormSchema';
import { DateTimePickerField } from '../../../utils/DatetimePickerField';

export const PeriodStep = () => {
  const { messages } = useLocalization();

  const {
    stepperProps,
    dateRange,
    setDateRange,
    startDatetimeThreshold,
    endDatetimeThreshold,
  } = useCreateReportContext();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const isInitializedRef = useRef(false);

  // Create default dates
  const defaultDates = useMemo(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const defaultStart = new Date(
      Date.UTC(
        yesterday.getFullYear(),
        yesterday.getMonth(),
        yesterday.getDate(),
        0,
        0,
        0,
        0,
      ),
    );

    const defaultEnd = new Date(
      Date.UTC(
        yesterday.getFullYear(),
        yesterday.getMonth(),
        yesterday.getDate(),
        23,
        59,
        59,
        999,
      ),
    );

    return { defaultStart, defaultEnd };
  }, []);

  const formSchema = useMemo(
    () =>
      createReportTemplatePeriodFormSchema(
        startDatetimeThreshold,
        endDatetimeThreshold,
        formatDateTimeWithTimeZone(startDatetimeThreshold),
        formatDateTimeWithTimeZone(endDatetimeThreshold),
      ),
    [startDatetimeThreshold, endDatetimeThreshold, formatDateTimeWithTimeZone],
  );

  const form = useForm<reportTemplatePeriodValues>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      start_datetime: dateRange?.from || defaultDates.defaultStart,
      end_datetime: dateRange?.to || defaultDates.defaultEnd,
    },
  });

  // Initialize form values only once
  useEffect(() => {
    if (!isInitializedRef.current) {
      const startValue = dateRange?.from || defaultDates.defaultStart;
      const endValue = dateRange?.to || defaultDates.defaultEnd;

      form.setValue('start_datetime', startValue, { shouldValidate: false });
      form.setValue('end_datetime', endValue, { shouldValidate: false });

      // Initialize context state if not already set
      if (!dateRange) {
        setDateRange({
          from: startValue,
          to: endValue,
        });
      }

      isInitializedRef.current = true;
    }
  }, [dateRange, defaultDates, form, setDateRange]);

  // Sync form changes back to context state (debounced to avoid conflicts)
  useEffect(() => {
    if (!isInitializedRef.current) return;

    const subscription = form.watch((values) => {
      if (values.start_datetime && values.end_datetime) {
        setDateRange({
          from: values.start_datetime,
          to: values.end_datetime,
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [form, setDateRange]);

  // Only sync context changes to form when switching modes (not during normal form interaction)
  useEffect(() => {
    if (!isInitializedRef.current) return;

    // Only update form if the context values are significantly different from form values
    const currentFormValues = form.getValues();
    const contextFromTime = dateRange?.from?.getTime();
    const contextToTime = dateRange?.to?.getTime();
    const formFromTime = currentFormValues.start_datetime?.getTime();
    const formToTime = currentFormValues.end_datetime?.getTime();

    if (
      dateRange?.from &&
      dateRange?.to &&
      (contextFromTime !== formFromTime || contextToTime !== formToTime)
    ) {
      // Don't validate immediately to avoid clearing validation errors
      form.setValue('start_datetime', dateRange.from, {
        shouldValidate: false,
      });
      form.setValue('end_datetime', dateRange.to, { shouldValidate: false });
    }
  }, [dateRange?.from, dateRange?.to, form]);

  const onPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    stepperProps.prevStep();
  };

  const onNext = (data: reportTemplatePeriodValues) => {
    setDateRange({
      from: data.start_datetime,
      to: data.end_datetime,
    });
    stepperProps.nextStep();
  };

  return (
    <div className="grid grid-rows-[auto_1fr_auto] h-full">
      <StepperHeadline
        headerText={messages.reportsCreateReportPeriodTitle}
        subHeaderText={messages.reportsCreateReportPeriodSubtitle}
      />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onNext)}
          className="flex flex-col space-y-8 pt-4 h-full flex-grow"
        >
          <div className="h-[85%]">
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="start_datetime"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>
                      {messages.reportsCreateReportStartDateLabel ||
                        'Start Date'}{' '}
                      *
                    </FormLabel>
                    <FormControl>
                      <DateTimePickerField
                        value={field.value}
                        onChange={(value) => {
                          field.onChange(value);
                          // Trigger validation after a short delay to ensure value is set
                          setTimeout(() => {
                            form.trigger('start_datetime');
                            form.trigger('end_datetime');
                          }, 0);
                        }}
                        dateLimit="past"
                        startDatetimeThreshold={startDatetimeThreshold}
                        endDatetimeThreshold={endDatetimeThreshold}
                        className="w-80"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_datetime"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>
                      {messages.reportsCreateReportEndDateLabel || 'End Date'} *
                    </FormLabel>
                    <FormControl>
                      <DateTimePickerField
                        value={field.value}
                        onChange={(value) => {
                          field.onChange(value);
                          // Trigger validation after a short delay to ensure value is set
                          setTimeout(() => {
                            form.trigger('start_datetime');
                            form.trigger('end_datetime');
                          }, 0);
                        }}
                        dateLimit="past"
                        startDatetimeThreshold={startDatetimeThreshold}
                        endDatetimeThreshold={endDatetimeThreshold}
                        className="w-80"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </form>
      </Form>
      <StepperFooter
        onPrevious={onPrevious}
        onNext={form.handleSubmit(onNext)}
        stepperProps={stepperProps}
      />
    </div>
  );
};
